import React, { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Sparkles, Star, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, X } from 'lucide-react';

export const AiModelsPanel = ({ data, weekStart, line, reload }) => {
  const [priorities, setPriorities] = useState({});
  const [showPri, setShowPri] = useState(false);
  const [savingPri, setSavingPri] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState('');

  useEffect(() => {
    api.get('/optimizer/priorities').then(({ data: d }) => setPriorities(d.priorities || {})).catch(() => {});
  }, []);

  const togglePriority = (lineKey, sid) => {
    setPriorities(prev => {
      const list = prev[lineKey] || [];
      return { ...prev, [lineKey]: list.includes(sid) ? list.filter(x => x !== sid) : [...list, sid] };
    });
  };

  const savePriorities = async () => {
    setSavingPri(true);
    try {
      await api.put('/optimizer/priorities', { priorities });
      toast.success('Staff priorities saved — Smart Fill and AI models will honor them');
    } catch { toast.error('Failed to save priorities'); }
    setSavingPri(false);
  };

  const generate = async () => {
    setGenerating(true); setResult(null);
    try {
      const { data: d } = await api.post('/optimizer/model', { week_start: weekStart, ...(line ? { service_line: line } : {}) });
      setResult(d);
      if (d.message) toast.info(d.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI modeling failed — try again');
    }
    setGenerating(false);
  };

  const apply = async (sc) => {
    if (!window.confirm(`Apply "${sc.name}" — ${sc.moves.length} moves as DRAFT shifts? Staff won't see them until you publish.`)) return;
    setApplying(sc.name);
    try {
      const { data: d } = await api.post('/optimizer/model/apply', { moves: sc.moves, scenario_name: sc.name });
      toast.success(`${d.applied} draft moves applied to the schedule`);
      if (d.errors?.length) toast.warning(`${d.errors.length} moves skipped: ${d.errors[0]}`);
      setResult(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    setApplying('');
  };

  const lineStaff = (lk) => data.staff.filter(s => s.service_lines.includes(lk));

  return (
    <div className="space-y-4" data-testid="ai-models-panel">
      {/* Priorities editor */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl">
        <button onClick={() => setShowPri(v => !v)} className="w-full flex items-center gap-2 p-4 text-left" data-testid="priorities-toggle">
          <Star className="h-4 w-4 text-[#FFD66B]" />
          <span className="text-sm font-bold flex-1">Staff priorities per service line</span>
          <span className="text-[10px] text-white/40">{Object.values(priorities).flat().length} set</span>
          {showPri ? <ChevronUp className="h-4 w-4 text-white/40" /> : <ChevronDown className="h-4 w-4 text-white/40" />}
        </button>
        {showPri && (
          <div className="px-4 pb-4 space-y-3">
            <p className="text-xs text-white/40">Tap staff in the order you want them prioritized for each line. AI scenarios and Smart Fill rankings honor this order.</p>
            {data.lines.map(lk => (
              <div key={lk}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-1.5">{data.line_labels[lk]}</div>
                <div className="flex flex-wrap gap-1.5">
                  {lineStaff(lk).map(s => {
                    const rank = (priorities[lk] || []).indexOf(s.id);
                    return (
                      <button key={s.id} onClick={() => togglePriority(lk, s.id)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${rank >= 0 ? 'bg-[#FFD66B] text-[#1A1325] border-[#FFD66B]' : 'text-white/60 border-white/15 hover:border-white/40'}`}
                              data-testid={`priority-${lk}-${s.id}`}>
                        {rank >= 0 && <span className="font-black mr-1">#{rank + 1}</span>}{s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button onClick={savePriorities} disabled={savingPri}
                    className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-bold flex items-center gap-2"
                    data-testid="priorities-save">
              {savingPri && <Loader2 className="h-4 w-4 animate-spin" />} Save priorities
            </button>
          </div>
        )}
      </div>

      {/* Generate */}
      {!result && (
        <button onClick={generate} disabled={generating}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#FFD66B] to-[#E07A5F] text-[#1A1325] text-base font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="ai-generate-btn">
          {generating ? <><Loader2 className="h-5 w-5 animate-spin" /> Modeling scenarios — analyzing {data.kpis.gap_count} gaps…</> : <><Sparkles className="h-5 w-5" /> Generate 3 AI schedule scenarios</>}
        </button>
      )}

      {/* Scenario cards */}
      {result?.scenarios?.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-white/40">{result.scenarios.length} scenarios modeled against {result.gap_count} gaps. Applying writes DRAFT shifts only.</p>
            <button onClick={() => setResult(null)} className="text-xs text-white/40 hover:text-white flex items-center gap-1" data-testid="ai-clear-btn"><X className="h-3 w-3" /> Clear</button>
          </div>
          <div className="grid lg:grid-cols-3 gap-3">
            {result.scenarios.map((sc, si) => (
              <div key={si} className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex flex-col gap-3" data-testid={`scenario-${si}`}>
                <div>
                  <h3 className="font-bold text-[#FFD66B] flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> {sc.name}</h3>
                  <p className="text-xs text-white/50 mt-1 leading-relaxed">{sc.narrative}</p>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  <div className="bg-white/5 rounded-lg py-1.5">
                    <div className="text-lg font-bold">{sc.kpis.gaps_filled}<span className="text-white/30 text-xs">/{sc.kpis.gaps_total}</span></div>
                    <div className="text-[9px] uppercase text-white/40">Gaps filled</div>
                  </div>
                  <div className="bg-white/5 rounded-lg py-1.5">
                    <div className="text-lg font-bold">+{sc.kpis.hours_added}h</div>
                    <div className="text-[9px] uppercase text-white/40">Hours</div>
                  </div>
                  <div className="bg-white/5 rounded-lg py-1.5">
                    <div className="text-lg font-bold">{sc.kpis.projected_avg_utilization}%</div>
                    <div className="text-[9px] uppercase text-white/40">Proj. util</div>
                  </div>
                </div>
                {sc.warnings.length > 0 && (
                  <div className="text-[10px] text-[#FFD66B] bg-[#3A2F10]/50 rounded-lg p-2 space-y-0.5">
                    {sc.warnings.map((w, wi) => <div key={wi} className="flex gap-1"><AlertTriangle className="h-3 w-3 shrink-0" /> {w}</div>)}
                  </div>
                )}
                <div className="flex-1 space-y-1 max-h-52 overflow-y-auto pr-1">
                  {sc.moves.map((m, mi) => (
                    <div key={mi} className="text-[11px] bg-white/5 rounded-lg px-2 py-1.5 leading-snug">{m.label}</div>
                  ))}
                  {sc.moves.length === 0 && <p className="text-xs text-white/30">No viable moves in this scenario.</p>}
                </div>
                <button onClick={() => apply(sc)} disabled={!!applying || sc.moves.length === 0}
                        className="h-11 rounded-xl bg-[#FFD66B] text-[#1A1325] font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
                        data-testid={`scenario-${si}-apply`}>
                  {applying === sc.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Apply as drafts ({sc.moves.length})
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      {result && !result.scenarios?.length && (
        <div className="text-center py-10 text-white/40">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-[#34C98E]" />
          <p className="text-sm">{result.message || 'Nothing to model this week.'}</p>
        </div>
      )}
    </div>
  );
};

export default AiModelsPanel;
